/**
 * TEXTS THIS LIBRARY HOLDS, MAY PUBLISH, AND HAS NOT DECIDED WHERE TO PUT.
 *
 * There were two ways a reader could be told a text is missing and they cover two of the three
 * real cases. `GRANTS` with `permitted: false` covers an edition fetched and withheld because
 * somebody has not said yes — the Talmud. `ABSENT_BRANCHES` covers a branch with nothing at all,
 * and why. **Neither covers a text that is here, is licensed, and is waiting on us.**
 *
 * Three files in `corpora/` hold 16,990 such passages — it was six files and 33,125 until
 * 2026-08-17, when the deuterocanon and Targum Onkelos were published and the Armenian turned out
 * to have been published all along. Every licence permits commercial use under this project's own
 * rules, every original is paired, and `pipeline/qa-staged.ts` verifies them on each run — which is
 * how the gap between this sentence and the files was found, the sentence having stayed at 33,125
 * while 16,015 of those passages went on a page. They are unpublished because registering a corpus is a claim about what the library
 * contains and that claim belongs to a person — see STAGED.md, where the decisions are written out.
 *
 * SO THE READER GOT SILENCE, AND THE SILENCE WAS THE WRONG SHAPE. A reader looking for the Syriac
 * New Testament finds nothing, and the true reason is neither "nobody would license it" nor "no
 * such text exists" but "it is on the shelf behind the counter". The sharpest case arrived on
 * 2026-08-16, when `NT.shorterCanon` began telling Assyrian readers that their canon is 22 books
 * rather than 27 — correct, useful, and printed while their actual Bible sat in `corpora/` with
 * nothing saying so.
 *
 * WHY THIS IS NOT `ABSENT_BRANCHES`. That file's own header rules it out: "A branch whose text is
 * fetched and awaiting a decision about how to register it is not absent either; it is a decision,
 * and putting it here would tell a reader something false about the world when the truth is
 * something about us." Right, and the conclusion drawn from it was silence. This is the same
 * distinction the `addressing` blocker makes inside that file — an obstacle that is OURS is still
 * worth stating — applied one level up, to a whole text rather than a branch.
 *
 * WHAT DOES NOT BELONG HERE. Anything whose licence is the obstacle: that is a grant question and
 * saying "awaiting a decision" about it would be flattering ourselves at a publisher's expense.
 * A test enforces it.
 */
import type { Tradition } from './absent.js';

export interface UnshelvedText {
  /** Which tradition's part of the panel this belongs in. */
  tradition: Tradition;
  /** The text as a reader would ask for it. */
  name: string;
  /** How many passages are held. Verified against the file by `pipeline/qa-staged.ts`. */
  passages: number;
  /** Both halves, because a text is its translation and its original and they are licensed apart. */
  licence: string;
  /**
   * The decision it waits on, stated as a decision rather than as an obstacle.
   *
   * Written so a reader can see it is not a refusal and not a gap in the world. "Awaiting a
   * decision" with no indication of what the decision IS reads as an excuse; naming it lets a
   * reader judge whether it is a real question, and most of these are.
   */
  detail: string;
}

/**
 * The six staged files, as a reader should hear about them.
 *
 * Counts are the floor `qa-staged.ts` checks, not a hand-written total, so a file that shrinks
 * fails the build rather than quietly making this page wrong.
 */
export const UNSHELVED: UnshelvedText[] = [
  {
    tradition: 'christianity',
    name: 'the Syriac New Testament, in the Peshitta with two English translations',
    passages: 7939,
    licence: 'All three parts public domain — the Syriac is ancient, Etheridge died in 1866 and Murdock in 1856',
    detail:
      'Held complete, and free to publish in full. What is undecided is what it should be called: the Bible of the Assyrian Church of the East, a second original beside the Greek, or a further reading of the New Testament already here. Those are three different claims about the library, and which one it is has not been settled.',
  },
  /*
   * THE ARMENIAN LEFT THIS LIST ON 2026-08-16, hours after joining it.
   *
   * It was here because it is a New Testament in a language with no English beside it, and that
   * looked like a decision somebody should make before a reader met an unreadable line. Then the
   * Coptic arrived in exactly the same condition and went straight in as a rendering, because by
   * then the library carried texts that are not in English — the Arabic and Salkinson's Hebrew.
   *
   * Two texts of the same kind, treated differently because of the order they were found in. The
   * Armenian is now a rendering on the New Testament like the Coptic, so a reader can see it; the
   * question of whether it should ALSO be a corpus in its own right stays in STAGED.md, where the
   * Peshitta's identical question already lives.
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * THE TWO OLD TESTAMENTS WHOSE NEW TESTAMENTS ARE ALREADY ON THE PAGE — added 2026-08-20.
   *
   * This list told a reader 16,990 passages are held and unshelved. **Measured against what
   * actually reaches a reader, the figure is 65,298**, and nearly all of the difference is these
   * two: `geez.json` holds 38,027 passages and ships 7,882; `georgian.json` holds 30,600 and ships
   * 7,947. In both cases what ships is the New Testament and what does not is everything else.
   *
   * The list's own header names this failure and predicted the direction: *"a hand-written count of
   * what is withheld goes stale in the direction that flatters."* It went stale the other way here
   * — not by publishing something and forgetting to subtract, but by fetching something and never
   * adding it. Both are the same defect, which is that the count is written by hand.
   *
   * **These are not the deuterocanon question.** `CLAUDE.md` reserves that one — whether this
   * library contains the deuterocanon at all — and it is about `septuagint.json` and
   * `deuterocanon.json`, which are already published as the `DEUTERO` shelf. This is the different
   * question `SOURCES.md` files these four staged texts under and `STAGED.md` says it never states:
   * **canon shape**. The Tewahedo canon is the largest in Christianity, and registering it as
   * sixty-six books would misdescribe it.
   */
  {
    tradition: 'christianity',
    name: 'the Geʽez Old Testament — the Orit, with Henok, Jubilees and Meqabyan',
    passages: 30130,
    /* Hyphenated because the test that checks both halves cleared reads `/CC-?BY/`, and this
       project writes the family both ways; `lib/licence-policy.ts` normalises them. */
    licence: 'CC-BY-SA 4.0 on the Geʽez, from Beta maṣāḥǝft at Hamburg — there is no second half, because there is no English of it',
    detail:
      'Forty-nine books outside the New Testament, whose Geʽez New Testament is already a rendering here. It holds the Orit complete at eight of eight — Genesis to Judges with Ruth, 7,148 passages — and the books that make this canon the largest in Christianity: Henok 1,058, Jubilees 1,025, and Meqabyan 1,378 across its three. An English of Meqabyan is held too, 867 passages from English Wikisource. The decision is canon shape: registering the Tewahedo Bible as sixty-six books would misdescribe it, and registering it as it stands is a claim about what this library contains. It is also the Orit that Beta Israel hold, which is why their row in the absent list says the Orit is answered and the writings that make them distinct are not.',
  },
  {
    tradition: 'christianity',
    name: 'the Georgian Old Testament',
    passages: 22648,
    licence: 'Public domain by age on the Old Georgian, a mediaeval translation — there is no second half, because there is no English of it',
    detail:
      'Thirty-nine books outside the New Testament, whose Georgian New Testament is already a rendering here. The same shape as the Geʽez beside it and the same open question: the Georgian Orthodox Church is on the roster as represented, and what represents it is half its Bible.',
  },
  {
    tradition: 'judaism',
    name: 'the Jerusalem Talmud, in Guggenheimer’s translation',
    passages: 2204,
    licence: 'CC-BY on both the English and the Aramaic',
    detail:
      'The other Talmud, and this one is free to publish in full. Whether it should appear while the Babylonian Talmud cannot is the open question: showing the Jerusalem one alone would say something about how the two stand to each other that nobody here has argued for, so the choice is between showing it, waiting, and showing it with that difference explained.',
  },
  {
    tradition: 'judaism',
    name: 'the Tosefta, the Mishneh Torah and Midrash Rabbah',
    passages: 6847,
    licence: 'Every version commercially usable — CC0, public domain and CC-BY',
    detail:
      'Three works of different kinds, fetched together because one source carries them. Each needs its own decision about where it sits beside the Mishnah, and the Tosefta additionally needs its uneven coverage stated: some tractates arrive nearly whole and others barely at all.',
  },
  /*
   * TARGUM ONKELOS AND THE DEUTEROCANON LEFT THIS LIST ON 2026-08-17, because they went on a page.
   *
   * Both decisions recorded here as open were answered that day. The deuterocanon became its own
   * shelf — `DEUTERO`, 5,915 passages, Brenton's Greek as the original with the Clementine Vulgate
   * and the CPDV beside it — and Onkelos became a READING on the Hebrew Bible, `targum` and
   * `targumEn`, attached to 5,813 verses.
   *
   * They are removed rather than edited because this list is what a reader is TOLD is held back,
   * and `qa-staged.ts` compares that claim against the files. Leaving them would have gone on
   * telling readers that 16,015 published passages were behind the counter — which it did, until
   * running that check surfaced a gap of 23,954 between what this page claimed and what was held.
   *
   * The lesson is the list's own: a hand-written count of what is withheld goes stale in the
   * direction that flatters, because publishing something is a good day and nobody thinks to
   * subtract it afterwards.
   */
];

/** Everything unshelved in one tradition, for the panel section that names it. */
export function unshelvedIn(tradition: Tradition): UnshelvedText[] {
  return UNSHELVED.filter(u => u.tradition === tradition);
}

/** The total a reader is told about, computed rather than written down twice. */
export function unshelvedPassages(): number {
  return UNSHELVED.reduce((n, u) => n + u.passages, 0);
}
